/* eslint-disable no-console */
import { FormEventHandler, useState } from "react";
import _ from "lodash";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";

import api from "../services/api";
import useAppTranslations from "../hooks/useAppTranslations";
import Landing from "../components/Landing";
import { IPasswordRecoveryForm } from "../types/forms";
import useYupFormResolver from "../hooks/useYupFormResolver";
import FormTextField from "../components/FormTextField";
import Translate from "../components/Translate";

const initialFormState: IPasswordRecoveryForm = {
  email: ""
};

export default function PasswordRecoveryPage() {
  const { t } = useAppTranslations();
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState<string | null>(null);

  const yupFormValidation = yup.object({
    email: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:corp_email")
        })
      )
      .email(t("validation:valid_email"))
  });

  const yupFormResolver = useYupFormResolver<typeof yupFormValidation>(yupFormValidation);

  const formHook = useForm<IPasswordRecoveryForm>({
    mode: "onSubmit",
    resolver: yupFormResolver,
    values: initialFormState
  });

  const { watch } = formHook;
  const form = watch();

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = ev => {
    ev.preventDefault();
    formHook.handleSubmit(async data => {
      api
        .postPasswordRecovery(data.email, "recover-password")
        .then(response => {
          if (response.data.success)
            navigate("/email-confirmation", { state: { email: data.email } });
        })
        .catch(() => {
          setEmailError(data.email);
        });
    })(ev);
  };

  return (
    <Landing
      headerTitle={t("title:recovery_password")}
      submitButtonLabel={t(
        formHook.formState.isSubmitted
          ? "action:resend_email_confirmation"
          : "action:send_email_confirmation"
      )}
      handleCardFormSubmit={handleFormSubmit}
      CardFormContent={
        <Box
          className="rounded-2xl"
          sx={theme => ({
            py: 3,
            px: 4,
            background: theme.palette.background.default,
            border: `solid 1px ${theme.palette.grey[300]}`,

            [theme.breakpoints.down("sm")]: {
              border: "none",
              py: 1.5,
              px: 2
            }
          })}
        >
          <Box className="grow">
            <Typography variant="subtitle2">
              <Translate src="form:legend:access_form" />
            </Typography>

            <FormTextField
              id="email"
              label={t("form:field:corp_email")}
              placeholder={t("form:placeholder:corp_email")}
              fieldControl={formHook.control}
              fieldName="email"
              gutters={false}
            />
          </Box>
          {emailError && _.isEqual(emailError, form.email) ? (
            <Typography variant="body2" color="error">
              <Translate src="error:email_nonexistent" />
            </Typography>
          ) : null}
        </Box>
      }
    />
  );
}
